// Group I. 基本句型與語氣 Basics and Clause Types
// Based on "Grammar 90 Groups (GPT5) 5 Examples Each.txt"

export const group1Data = {
  id: "group_1",
  title: {
    tc: "基本句型與語氣",
    sc: "基本句型与语气",
    en: "Basic Sentence Patterns and Mood"
  },
  subtitle: {
    tc: "Basics and Clause Types",
    sc: "Basics and Clause Types", 
    en: "Basics and Clause Types"
  },
  icon: "📝",
  difficulty: "beginner",
  color: "from-blue-500 to-blue-600",
  topics: [
    {
      id: "statements",
      title: {
        tc: "陳述句",
        sc: "陈述句",
        en: "Statements"
      },
      description: {
        tc: "學習陳述句的基本結構和用法",
        sc: "学习陈述句的基本结构和用法",
        en: "Learn the basic structure and usage of statements"
      },
      icon: "💬",
      difficulty: "beginner",
      examples: [
        {
          id: "statements_1",
          sentence: "I finished my homework.",
          analysis: "I (S) finished (V) my homework (O).",
          translation: {
            tc: "我完成了我的作業。",
            sc: "我完成了我的作业。",
            en: "I finished my homework."
          }
        },
        {
          id: "statements_2", 
          sentence: "She is a designer.",
          analysis: "She (S) is (V) a designer (C).",
          translation: {
            tc: "她是一名設計師。",
            sc: "她是一名设计师。",
            en: "She is a designer."
          }
        },
        {
          id: "statements_3",
          sentence: "They moved last month.",
          analysis: "They (S) moved (V) last month (M-time).",
          translation: {
            tc: "他們上個月搬家了。",
            sc: "他们上个月搬家了。",
            en: "They moved last month."
          }
        },
        {
          id: "statements_4",
          sentence: "The meeting starts at nine.",
          analysis: "The meeting (S) starts (V) at nine (M-time).",
          translation: {
            tc: "會議在九點開始。",
            sc: "会议在九点开始。",
            en: "The meeting starts at nine."
          }
        },
        {
          id: "statements_5",
          sentence: "Our plan seems reasonable.",
          analysis: "Our plan (S) seems (V-link) reasonable (C).",
          translation: {
            tc: "我們的計劃似乎合理。",
            sc: "我们的计划似乎合理。",
            en: "Our plan seems reasonable."
          }
        },
        {
          id: "statements_6",
          sentence: "He stuffed a towel under his door so the smell would not seep into his room.",
          analysis: "He (S) stuffed (V) a towel (O) under his door (M) so the smell (S) would not seep (V) into his room (M).",
          translation: {
            tc: "他在門下塞了一條毛巾，這樣氣味就不會滲入他的房間。",
            sc: "他在门下塞了一条毛巾，这样气味就不会渗入他的房间。",
            en: "He stuffed a towel under his door so the smell would not seep into his room."
          }
        }
      ],
      teachingNotes: {
        tc: "要點：用於陳述事實/觀點；主語+謂語是核心，必要時加 C/M。",
        sc: "要点：用于陈述事实/观点；主语+谓语是核心，必要时加 C/M。",
        en: "Key points: Used for stating facts/opinions; Subject + Predicate is the core, add C/M when necessary."
      }
    },
    {
      id: "negatives",
      title: {
        tc: "否定句",
        sc: "否定句", 
        en: "Negatives"
      },
      description: {
        tc: "掌握否定句的構成規則",
        sc: "掌握否定句的构成规则",
        en: "Master the rules of forming negative sentences"
      },
      icon: "❌",
      difficulty: "beginner",
      examples: [
        {
          id: "negatives_1",
          sentence: "I do not like coffee.",
          analysis: "I (S) do not like (V) coffee (O).",
          translation: {
            tc: "我不喜歡咖啡。",
            sc: "我不喜欢咖啡。",
            en: "I do not like coffee."
          }
        },
        {
          id: "negatives_2",
          sentence: "She doesn't drive.",
          analysis: "She (S) does not drive (V).",
          translation: {
            tc: "她不會開車。",
            sc: "她不会开车。",
            en: "She doesn't drive."
          }
        },
        {
          id: "negatives_3",
          sentence: "We didn't see anything.",
          analysis: "We (S) did not see (V) anything (O).",
          translation: {
            tc: "我們什麼都沒看到。",
            sc: "我们什么都没看到。",
            en: "We didn't see anything."
          }
        },
        {
          id: "negatives_4",
          sentence: "He isn't ready yet.",
          analysis: "He (S) is not (V) ready (C) yet (M).",
          translation: {
            tc: "他還沒準備好。",
            sc: "他还没准备好。",
            en: "He isn't ready yet."
          }
        },
        {
          id: "negatives_5",
          sentence: "There was no problem.",
          analysis: "There (dummy) was (V) no problem (S-notional).",
          translation: {
            tc: "沒有問題。",
            sc: "没有问题。",
            en: "There was no problem."
          }
        }
      ],
      teachingNotes: {
        tc: "要點：一般動詞用 do/does/did + not；be/情態直接否定；注意否定詞範圍。",
        sc: "要点：一般动词用 do/does/did + not；be/情态直接否定；注意否定词范围。",
        en: "Key points: Use do/does/did + not for regular verbs; be/modals directly negated; pay attention to scope of negation."
      }
    },
    {
      id: "yes_no_questions",
      title: {
        tc: "一般疑問句",
        sc: "一般疑问句",
        en: "Yes/No Questions"
      },
      description: {
        tc: "學習一般疑問句的構成和用法",
        sc: "学习一般疑问句的构成和用法",
        en: "Learn the structure and usage of yes/no questions"
      },
      icon: "❓",
      difficulty: "beginner",
      examples: [
        {
          id: "yes_no_1",
          sentence: "Do you play tennis?",
          analysis: "Do (Aux) you (S) play (V) tennis (O)?",
          translation: {
            tc: "你打網球嗎？",
            sc: "你打网球吗？",
            en: "Do you play tennis?"
          }
        },
        {
          id: "yes_no_2",
          sentence: "Are they at home?",
          analysis: "Are (V) they (S) at home (M)?",
          translation: {
            tc: "他們在家嗎？",
            sc: "他们在家吗？",
            en: "Are they at home?"
          }
        },
        {
          id: "yes_no_3",
          sentence: "Did she call you?",
          analysis: "Did (Aux) she (S) call (V) you (O)?",
          translation: {
            tc: "她打電話給你了嗎？",
            sc: "她打电话给你了吗？",
            en: "Did she call you?"
          }
        },
        {
          id: "yes_no_4",
          sentence: "Has he finished?",
          analysis: "Has (Aux) he (S) finished (V)?",
          translation: {
            tc: "他完成了嗎？",
            sc: "他完成了吗？",
            en: "Has he finished?"
          }
        },
        {
          id: "yes_no_5",
          sentence: "Can we start now?",
          analysis: "Can (Modal) we (S) start (V) now (M)?",
          translation: {
            tc: "我們現在可以開始嗎？",
            sc: "我们现在可以开始吗？",
            en: "Can we start now?"
          }
        }
      ],
      teachingNotes: {
        tc: "要點：助動詞/情態/BE 置前；主語緊跟其後。",
        sc: "要点：助动词/情态/BE 置前；主语紧跟其后。",
        en: "Key points: Auxiliary/modals/BE come first; subject follows immediately."
      }
    },
    {
      id: "wh_questions",
      title: {
        tc: "特殊疑問句",
        sc: "特殊疑问句",
        en: "Wh- Questions"
      },
      description: {
        tc: "學習特殊疑問句的構成和用法",
        sc: "学习特殊疑问句的构成和用法",
        en: "Learn the structure and usage of wh-questions"
      },
      icon: "🔍",
      difficulty: "beginner",
      examples: [
        {
          id: "wh_1",
          sentence: "Where do you live?",
          analysis: "Where (Wh) do (Aux) you (S) live (V)?",
          translation: {
            tc: "你住在哪裡？",
            sc: "你住在哪里？",
            en: "Where do you live?"
          }
        },
        {
          id: "wh_2",
          sentence: "What did he say?",
          analysis: "What (Wh) did (Aux) he (S) say (V)?",
          translation: {
            tc: "他說了什麼？",
            sc: "他说了什么？",
            en: "What did he say?"
          }
        },
        {
          id: "wh_3",
          sentence: "When will the train arrive?",
          analysis: "When (Wh) will (Aux) the train (S) arrive (V)?",
          translation: {
            tc: "火車什麼時候到？",
            sc: "火车什么时候到？",
            en: "When will the train arrive?"
          }
        },
        {
          id: "wh_4",
          sentence: "Why are they upset?",
          analysis: "Why (Wh) are (V) they (S) upset (C)?",
          translation: {
            tc: "他們為什麼不高興？",
            sc: "他们为什么不高兴？",
            en: "Why are they upset?"
          }
        },
        {
          id: "wh_5",
          sentence: "How much does it cost?",
          analysis: "How much (Wh) does (Aux) it (S) cost (V)?",
          translation: {
            tc: "它要多少錢？",
            sc: "它要多少钱？",
            en: "How much does it cost?"
          }
        }
      ],
      teachingNotes: {
        tc: "要點：Wh- 放首位；若 Wh- 作主語則不倒裝。",
        sc: "要点：Wh- 放首位；若 Wh- 作主语则不倒装。",
        en: "Key points: Wh- comes first; if Wh- is subject, no inversion."
      }
    },
    {
      id: "imperatives",
      title: {
        tc: "祈使句",
        sc: "祈使句",
        en: "Imperatives"
      },
      description: {
        tc: "學習祈使句的構成和用法",
        sc: "学习祈使句的构成和用法",
        en: "Learn the structure and usage of imperatives"
      },
      icon: "📢",
      difficulty: "beginner",
      examples: [
        {
          id: "imperatives_1",
          sentence: "Please close the door.",
          analysis: "(You) (S-ellipsed) close (V) the door (O). Please (M).",
          translation: {
            tc: "請關門。",
            sc: "请关门。",
            en: "Please close the door."
          }
        },
        {
          id: "imperatives_2",
          sentence: "Sit down, please.",
          analysis: "(You) (S) sit (V) down (M).",
          translation: {
            tc: "請坐下。",
            sc: "请坐下。",
            en: "Sit down, please."
          }
        },
        {
          id: "imperatives_3",
          sentence: "Don't be late.",
          analysis: "(You) (S) do not be (V) late (C).",
          translation: {
            tc: "不要遲到。",
            sc: "不要迟到。",
            en: "Don't be late."
          }
        },
        {
          id: "imperatives_4",
          sentence: "Let's begin.",
          analysis: "Let us (V) begin (V).",
          translation: {
            tc: "讓我們開始吧。",
            sc: "让我们开始吧。",
            en: "Let's begin."
          }
        },
        {
          id: "imperatives_5",
          sentence: "Be careful on the stairs.",
          analysis: "(You) (S) be (V) careful (C) on the stairs (M).",
          translation: {
            tc: "在樓梯上要小心。",
            sc: "在楼梯上要小心。",
            en: "Be careful on the stairs."
          }
        }
      ],
      teachingNotes: {
        tc: "要點：主語 you 省略；否定用 Don't + V；Let's 提議含我們。",
        sc: "要点：主语 you 省略；否定用 Don't + V；Let's 提议含我们。",
        en: "Key points: Subject you omitted; negative uses Don't + V; Let's includes us."
      }
    },
    {
      id: "exclamatives",
      title: {
        tc: "感嘆句",
        sc: "感叹句",
        en: "Exclamatives"
      },
      description: {
        tc: "學習感嘆句的構成和用法",
        sc: "学习感叹句的构成和用法",
        en: "Learn the structure and usage of exclamatives"
      },
      icon: "😮",
      difficulty: "beginner",
      examples: [
        {
          id: "exclamatives_1",
          sentence: "What a wonderful idea!",
          analysis: "What a wonderful idea (C/S)!",
          translation: {
            tc: "多麼棒的主意！",
            sc: "多么棒的主意！",
            en: "What a wonderful idea!"
          }
        },
        {
          id: "exclamatives_2",
          sentence: "How beautiful the view is!",
          analysis: "How (Wh-degree) beautiful (C) the view (S) is (V)!",
          translation: {
            tc: "這景色多麼美麗！",
            sc: "这景色多么美丽！",
            en: "How beautiful the view is!"
          }
        },
        {
          id: "exclamatives_3",
          sentence: "What an expensive mistake that was!",
          analysis: "What an expensive mistake (C) that (S) was (V)!",
          translation: {
            tc: "那是多麼昂貴的錯誤！",
            sc: "那是多么昂贵的错误！",
            en: "What an expensive mistake that was!"
          }
        },
        {
          id: "exclamatives_4",
          sentence: "How quickly time flies!",
          analysis: "How (M) quickly (M) time (S) flies (V)!",
          translation: {
            tc: "時間過得多麼快！",
            sc: "时间过得多么快！",
            en: "How quickly time flies!"
          }
        },
        {
          id: "exclamatives_5",
          sentence: "Isn't this amazing!",
          analysis: "Is not (V) this (S) amazing (C)!",
          translation: {
            tc: "這不是很驚人嗎！",
            sc: "这不是很惊人吗！",
            en: "Isn't this amazing!"
          }
        }
      ],
      teachingNotes: {
        tc: "要點：What + (a/an) + Adj + N / How + Adj/Adv；語氣強烈。",
        sc: "要点：What + (a/an) + Adj + N / How + Adj/Adv；语气强烈。",
        en: "Key points: What + (a/an) + Adj + N / How + Adj/Adv; strong emotion."
      }
    }
  ]
};
